type WhiteLabelOptions = {
  apiUrl?: string
  assetUrl?: string
}

// sessionStorage key under which the first-load overrides are captured, so a
// later param-less navigation (internal route, 3DS return) keeps talking to the
// merchant proxy instead of silently reverting to Yuno's default origin.
const STORAGE_KEY = 'sdk-payments-white-label'

/**
 * Hosts explicitly approved to receive white-label traffic. Because `assetUrl`
 * drives the SDK script origin (loadScript baseUrl), an un-validated value is a
 * remote-script-execution vector. The list is empty unless a merchant proxy
 * host is provisioned per environment, so white-label is off by default.
 */
const getAllowedHosts = (): Set<string> =>
  new Set(
    (process.env.REACT_APP_WHITE_LABEL_ALLOWED_HOSTS ?? '')
      .split(',')
      .map((host) => host.trim().toLowerCase())
      .filter(Boolean)
  )

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

// True only when the checkout page itself is served from a loopback origin. Used
// to gate the http override exception so it applies to genuine local proxy
// testing but never to a crafted link opened on a deployed (real-host) checkout.
const isLoopbackPage = (): boolean =>
  LOOPBACK_HOSTS.has((window.location.hostname ?? '').toLowerCase())

const validateAllowedUrl = (
  value: string | null | undefined,
  allowedHosts: Set<string>
): string | undefined => {
  if (!value) return undefined

  try {
    const url = new URL(value)
    const host = url.hostname.toLowerCase().replace(/\.$/, '')
    // https is required everywhere. http is tolerated only for a loopback override
    // AND only when the page is itself on loopback, so a local white-label proxy
    // (e.g. http://localhost:9090) works without a TLS cert while a crafted
    // ?assetUrl=http://localhost link on deployed checkout stays rejected.
    const schemeAllowed =
      url.protocol === 'https:' ||
      (url.protocol === 'http:' && LOOPBACK_HOSTS.has(host) && isLoopbackPage())
    if (!schemeAllowed) return undefined
    return allowedHosts.has(host) ? value : undefined
  } catch {
    return undefined
  }
}

const persistOptions = (options: WhiteLabelOptions): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(options))
  } catch {
    // sessionStorage unavailable (private mode / disabled) — non-fatal, the
    // options just won't survive a param-less navigation.
  }
}

/**
 * Re-reads the first-load overrides captured in sessionStorage and re-validates
 * each one against the current allowlist, so a stale or tampered entry can never
 * point the SDK/BFF off the approved hosts.
 */
const readPersistedOptions = (allowedHosts: Set<string>): WhiteLabelOptions | undefined => {
  let raw: string | null = null
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return undefined
  }
  if (!raw) return undefined

  try {
    const parsed = JSON.parse(raw) as WhiteLabelOptions
    const apiUrl = validateAllowedUrl(parsed.apiUrl, allowedHosts)
    const assetUrl = validateAllowedUrl(parsed.assetUrl, allowedHosts)
    if (!apiUrl && !assetUrl) return undefined
    return { apiUrl, assetUrl }
  } catch {
    return undefined
  }
}

const computeWhiteLabelOptions = (): WhiteLabelOptions | undefined => {
  const allowedHosts = getAllowedHosts()
  if (!allowedHosts.size) return undefined

  const params = new URLSearchParams(window.location.search)
  const apiUrl = validateAllowedUrl(params.get('apiUrl'), allowedHosts)
  const assetUrl = validateAllowedUrl(params.get('assetUrl'), allowedHosts)

  if (apiUrl || assetUrl) {
    const options = { apiUrl, assetUrl }
    persistOptions(options)
    return options
  }

  // Params absent on this navigation — fall back to what the first load captured
  // so the whole white-label session stays on the merchant proxy.
  return readPersistedOptions(allowedHosts)
}

// The allowlist env and the entry URL are fixed for the page lifetime, so the
// resolved options are computed once and reused by every SDK-init call site.
let optionsCache: { value: WhiteLabelOptions | undefined } | undefined

export const getWhiteLabelOptions = (): WhiteLabelOptions | undefined => {
  if (!optionsCache) optionsCache = { value: computeWhiteLabelOptions() }
  return optionsCache.value
}

// Test-only: clears the lifetime memo so a spec can re-evaluate under a fresh
// env/URL (and to emulate a full page reload while sessionStorage survives).
export const resetWhiteLabelCache = (): void => {
  optionsCache = undefined
}

/**
 * Normalized sub-path (base path) of a white-label override URL — the pathname
 * with any trailing slash removed, or `''` for a root mount. Single source of
 * truth for both the router basename and the BFF host rewrite so they can't
 * drift onto different sub-paths.
 */
export const getWhiteLabelBasePath = (source: string): string => {
  try {
    return new URL(source).pathname.replace(/\/$/, '')
  } catch {
    return ''
  }
}

/**
 * Router basename for white-label sub-path hosting. A merchant proxy serves the
 * checkout under a sub-path (e.g. `some.domain/any/path/payment`) and passes
 * that same base in the `apiUrl`/`assetUrl` override, so its pathname is the
 * basename react-router needs to match `/payment`, `/enroll`, … against the
 * prefixed location. Returns `undefined` for a root mount (checkout.y.uno).
 */
export const getWhiteLabelBasename = (): string | undefined => {
  const options = getWhiteLabelOptions()
  const source = options?.apiUrl ?? options?.assetUrl
  if (!source) return undefined

  const basename = getWhiteLabelBasePath(source)
  if (!basename) return undefined

  // Only honor the basename when the checkout is actually served under it. If
  // the serving path and the apiUrl/assetUrl sub-path disagree, degrade to a
  // root mount so routes still resolve instead of rendering a blank no-match.
  const path = window.location.pathname
  const servedUnderBasename = path === basename || path.startsWith(`${basename}/`)
  return servedUnderBasename ? basename : undefined
}
